import { REGISTRATION_FORM_DEFINITION_ID } from "@/constants/constants";
import useUpdateRegistrationStatus from "@/hooks/useUpdateRegistrationStatus";
import useUserRegisteredSessions from "@/hooks/useUserRegisteredSessions";
import { faCheckCircle, faCircle } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown, faFileMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, CircularProgress, Collapse, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import { GetFieldLookupItem, useFormLookups, useFormMetadata } from '@ngt/forms';
import React from "react";
import { Registration, RegistrationSession, Session, ToggleRegistrationStatusResponse } from "../../api/dtos";

const getDelegateType = (registration: Registration) => {

    if (registration.attendanceTypeForLabel !== undefined && registration.attendanceTypeForLabel !== null && registration.attendanceTypeForLabel !== "")
    {
        return registration.attendanceTypeForLabel;
    }

    return null;

}

interface IRegistrationAttendeesProps {
    sessionId: number | undefined;
    registrationSessions: RegistrationSession[] | undefined;
}

const RegistrationAttendees: React.FC<IRegistrationAttendeesProps> = ({ sessionId, registrationSessions }) => {
    if (!sessionId) {
        return null;
    }

    const session = registrationSessions?.filter(rs => rs.sessionId == sessionId);
    if (session?.length !== undefined && session?.length > 0) {
        return (
            <Box
                sx={{
                    width: '100%',
                    textAlign: 'center'
                }}
            >
                {session[0].numberOfAttendees}
            </Box>
        );
    }

    return null;
};

interface IExpandingRegoTableRowProps {
    registration: Registration;
    sessions?: Session[];
    handleMutate?: () => Promise<void>;
    handleRegistrationClick: (registrationId: number) => void;
}

const ExpandingRegoTableRow: React.FC<IExpandingRegoTableRowProps> = ({
    registration, 
    sessions, 
    handleMutate,
    handleRegistrationClick
}) => {
    const errorLight = '#ffd2d2';
    const errorLightHover = '#ffa5a5'
    const [open, setOpen] = React.useState(false);
    const {registrationSessions, loading: loadingSessions} = useUserRegisteredSessions(open ? registration.id! : undefined);
    const [, setResponse] = React.useState<ToggleRegistrationStatusResponse | undefined>();

    const theme = useTheme();

    const {updateRegistration} = useUpdateRegistrationStatus();

    const onUpdate = async () => {
        const res = await updateRegistration(registration.id);
        setResponse(res);
        if (handleMutate) {
            await handleMutate();
        }
    }
    const amountValid = registration.amountOwing === undefined  ||  registration.amountOwing === 0;

    const formMetadata = useFormMetadata(REGISTRATION_FORM_DEFINITION_ID);
    const {data:lookups} = useFormLookups(formMetadata, { revalidateOnFocus: false, refreshWhenOffline: false, });

    return (
        <>
            <TableRow
                sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    ":hover": {
                        cursor: 'pointer',
                        backgroundColor: amountValid ? theme.palette.grey[100] : errorLightHover
                    },
                    backgroundColor: amountValid? 'inherit' : errorLight 
                }}
                onClick={() => setOpen(!open)}
            >
                <TableCell
                    sx={{
                        color: theme.palette.grey[500]
                    }}
                >
                    <FontAwesomeIcon 
                        size="lg" 
                        fixedWidth
                        icon={faChevronDown}
                        className={open ? "rotate" : "standard"}
                    />
                </TableCell>
                <TableCell>
                    {registration.lastName}
                </TableCell>
                <TableCell>
                    {registration.firstName}
                </TableCell>
                <TableCell>
                    {registration.institution}
                </TableCell>
                <TableCell>
                    {GetFieldLookupItem(lookups, 'delegateType', registration.delegateType)?.value?.toString() ?? registration.delegateType}
                </TableCell>
                <TableCell>
                    {GetFieldLookupItem(lookups, 'registrationAffiliation', registration.registrationAffiliation)?.value?.toString() ?? registration.registrationAffiliation}
                </TableCell>
                <TableCell
                    sx={{
                        textAlign: 'center'
                    }}
                >
                    {`$${registration.amountOwing ?? "0"}`}
                </TableCell>
                <TableCell
                    sx={{
                        textAlign: 'center',
                    }}
                >
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleRegistrationClick(registration.id!)}>
                            <FontAwesomeIcon icon={faFileMagnifyingGlass} />
                    </Button>
                </TableCell>
                <TableCell
                    sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        ":hover": {
                            cursor: 'pointer',
                            '&:hover':{
                                color: theme.palette.secondary.main,
                                backgroundColor: theme.palette.grey[300]  
                            }
                        },
                        p: 1
                    }}
                    onClick={(event) => {
                        onUpdate(); 
                        event.stopPropagation();
                    }}
                >
                {
                    registration.registered ?
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: 'success.light',
                                justifyContent: 'center'
                            }}
                        >
                            <FontAwesomeIcon size="2x" fixedWidth icon={faCheckCircle} />
                        </Box>
                        :
                        <Box
                            sx={{
                                display: 'flex',
                                color: 'text.disabled',
                                justifyContent: 'center'
                            }}
                        >
                            <FontAwesomeIcon size="2x" fixedWidth icon={faCircle} />
                        </Box>
                }
            </TableCell>
            </TableRow>
            <TableRow
                sx={{
                    border: 'none',
                    '& td':{
                        border: 'none'
                    }
                }}
            >
                <TableCell 
                    sx={{
                        p: 0
                    }}
                    colSpan={7}
                >
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box 
                            sx={{ 
                                m: 1,
                                p: 2,
                                borderBottom: '1px solid rgba(224, 224, 224, 1)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}
                        >
                            {
                                loadingSessions && (
                                    <CircularProgress />
                                )
                            }
                            {
                                !loadingSessions && (
                                    <>
                                        <Typography 
                                            variant="h6" 
                                            component="div"
                                            sx={{
                                                mb: 2
                                            }}
                                        >
                                            Sessions
                                        </Typography>
                                        <Table 
                                            sx={{ 
                                                minWidth: 650 ,
                                                maxWidth: 'max-content',
                                                mb: 3
                                            }}
                                            size="small"
                                        >
                                            <TableHead>
                                                <TableRow
                                                    sx={{
                                                        '& th':{
                                                            border: 'none'
                                                        },
                                                        borderBottom: 'solid 2px'
                                                    }}
                                                >
                                                    <TableCell>
                                                        Session
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                    >
                                                        Attending
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                    >
                                                        Additional Attendees
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                            {
                                                sessions && (
                                                    sessions.map((session, index) => {
            
                                                        if (session.disabled != true)
                                                        {
                                                            return (
            
                                                                <TableRow
                                                                    sx={{
                                                                        '& th':{
                                                                            border: 'none'
                                                                        },
                                                                        backgroundColor: (index % 2 === 0) ? theme.palette.grey[100] : 'inherit'
                                                                    }}
                                                                >
                                                                    <TableCell
                                                                        sx={{
                                                                            textAlign: 'left',
                                                                        }}
                                                                        key={`session-name-${session.id}-${registration.id}`}
                                                                    >
                                                                        {session.name}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{
                                                                            textAlign: 'center',
                                                                        }}
                                                                        key={`session-heading-${session.id}-${registration.id}`}
                                                                    >
                                                                        {
                                                                            registrationSessions?.some(s => s.sessionId == session.id) ? 
                                                                                <Box
                                                                                    sx={{
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        color: 'success.light',
                                                                                        justifyContent: 'center'
                                                                                    }}
                                                                                >
                                                                                    <FontAwesomeIcon size="2x" fixedWidth icon={faCheckCircle} />
                                                                                </Box>
                                                                                :
                                                                                <Box
                                                                                    sx={{
                                                                                        display: 'flex',
                                                                                        color: 'text.disabled',
                                                                                        justifyContent: 'center'
                                                                                    }}
                                                                                >
                                                                                    <FontAwesomeIcon size="2x" fixedWidth icon={faCircle} />
                                                                                </Box>
                                                                        }
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{
                                                                            textAlign: 'center',
                                                                        }}
                                                                        key={`session-attendee-${session.id}-${registration.id}`}
                                                                    >
                                                                        <RegistrationAttendees 
                                                                            sessionId={session.id} 
                                                                            registrationSessions={registrationSessions} 
                                                                        />
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        }
                                                        
                                                    })
                                                )
                                            }
                                            </TableBody>
                                        </Table>
                                    </>
                                )
                            }
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default ExpandingRegoTableRow;
